import React, { useContext, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FormFields } from '@App/forms/v2/common/form-field'
import { FormFieldTypes } from '@App/enum/form-field-types'
import FormFieldFromProps from '@Fields/v2/FormFieldFromProps'
import './index.scss'
import { StoresContext } from '@App/contexts'
import CatalogTransferWizardHeader from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferWizard/CatalogTransferWizardHeader'
import { z } from 'zod'
import populateSchema from '@App/forms/v2/helpers/populate-schema'
import { zodResolver } from '@hookform/resolvers/zod'

const catalogTransferAddtlInfoSchema = z.object({
    effectiveDate: z.date(),
    writerNames: z.string().optional(),
    supplementalInfo: z.string().optional(),
})

type CatalogTransferAdditionalInfo = z.infer<typeof catalogTransferAddtlInfoSchema>

const catalogTransferFormFields: FormFields<CatalogTransferAdditionalInfo> = {
    ['effectiveDate']: {
        name: 'effectiveDate',
        label: 'Date',
        type: FormFieldTypes.DATEPICKER,
    },
    ['writerNames']: {
        name: 'writerNames',
        label: 'Writer Names',
        type: FormFieldTypes.TEXTAREA,
    },
    ['supplementalInfo']: {
        name: 'supplementalInfo',
        label: 'Supplemental Information',
        type: FormFieldTypes.TEXTAREA,
    },
}

const populatedFormFields: FormFields<CatalogTransferAdditionalInfo> = populateSchema(
    catalogTransferFormFields,
    catalogTransferAddtlInfoSchema
)

const AdditionalInfoStep = () => {
    const { catalogTransferWizardStore } = useContext(StoresContext)

    const methods = useForm<CatalogTransferAdditionalInfo>({
        resolver: zodResolver(catalogTransferAddtlInfoSchema),
        mode: 'onChange',
        defaultValues: {
            effectiveDate: catalogTransferWizardStore.currentCatalogTransfer?.effectiveDate
                ? new Date(catalogTransferWizardStore.currentCatalogTransfer.effectiveDate)
                : undefined,
            writerNames: catalogTransferWizardStore.currentCatalogTransfer?.writerNames || undefined,
            supplementalInfo: catalogTransferWizardStore.currentCatalogTransfer?.supplementalInformation || undefined,
        },
    })

    useEffect(() => {
        const callback = methods.subscribe({
            formState: {
                values: true,
                isValid: true,
            },
            callback: ({ values, isValid }) => {
                catalogTransferWizardStore.updateCatalogTransferDTO({ ...values })
                catalogTransferWizardStore.setAddtlInfoFormValid(isValid ?? false)
            },
        })
        return () => callback()
    }, [methods.subscribe])

    // Make sure the validation runs on mount to set the initial state correctly
    useEffect(() => {
        methods.trigger().then()
    }, [])

    return (
        <div className="ct-additional-info-step ct-wizard-step-container">
            <CatalogTransferWizardHeader title="Effective Date" />
            <section className="additional-info-form">
                <FormProvider {...methods}>
                    <form>
                        <div className="effective-date">
                            <h5>
                                Effective Date <br />
                                Select the date that the new administrator's term begins.
                            </h5>
                            <FormFieldFromProps field={populatedFormFields.effectiveDate} key="effectiveDate" />
                            <p>
                                It may take up to two months for the acquired works to appear in the new claimant's
                                Member catalog.
                            </p>
                        </div>
                        <div className="writer-name">
                            <p>
                                <strong>Writer Names (Optional)</strong>
                                <br />
                                Add one or more writers associated with the works you’re acquiring.
                            </p>
                            <FormFieldFromProps field={populatedFormFields.writerNames} key="writerName" />
                        </div>
                        <div className="supplemental-info">
                            <p>
                                <strong>Supplemental Information (Optional)</strong>
                                <br />
                                Any additional details that may be useful in processing the catalog transfer.
                            </p>
                            <FormFieldFromProps field={populatedFormFields.supplementalInfo} key="supplementalInfo" />
                        </div>
                    </form>
                </FormProvider>
            </section>
        </div>
    )
}

export default AdditionalInfoStep
